import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRole } from "@/contexts/RoleContext";
import { supabase, executeWithRetry } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Package, ShoppingCart, CheckCircle2, Copy, Printer, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CatalogItem {
  id: string;
  name: string;
  unit: string;
  created_at: string;
}

interface MaterialRequest {
  id: string;
  location_id: string | null;
  items: Array<{
    item_id: string;
    name: string;
    requested_qty: number;
    unit: string;
  }>;
  notes: string | null;
  status: string;
  created_at: string;
  location_address?: string;
}

interface GroupedRequest {
  item_id: string;
  item_name: string;
  unit: string;
  total_quantity: number;
  locations: Array<{
    location_id: string;
    location_address: string;
    quantity: number;
    request_id: string;
  }>;
}

export function WarehouseManagement() {
  const { t } = useTranslation();
  const { currentOrgId } = useRole();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [materialRequests, setMaterialRequests] = useState<MaterialRequest[]>([]);
  const [groupedRequests, setGroupedRequests] = useState<GroupedRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [newProductUnit, setNewProductUnit] = useState("szt");
  
  // CRITICAL: Ref do śledzenia poprzedniego currentOrgId - zapobiega resetowaniu przy remount
  const previousOrgIdRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  const fetchCatalog = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const { data, error } = await supabase
        .from("cleaning_catalog")
        .select("*")
        .eq("org_id", currentOrgId)
        .order("name", { ascending: true });

      if (error) throw error;
      setCatalogItems(data || []);
    } catch (error) {
      console.error("Error fetching catalog:", error);
      toast.error(t("admin.warehouse.catalogFetchError"));
    }
  }, [currentOrgId, t]);

  const fetchMaterialRequests = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const { data, error } = await supabase
        .from("material_requests")
        .select(`
          id,
          location_id,
          items,
          notes,
          status,
          created_at,
          cleaning_locations(address)
        `)
        .eq("org_id", currentOrgId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const requests = (data || []).map((req: any) => ({
        id: req.id,
        location_id: req.location_id,
        items: Array.isArray(req.items) ? req.items : [],
        notes: req.notes ?? null,
        status: req.status,
        created_at: req.created_at,
        location_address: req.cleaning_locations?.address || "",
      }));

      setMaterialRequests(requests);

      // Group requests by product
      const grouped = new Map<string, GroupedRequest>();
      const locationMap = new Map<string, string>();

      requests.forEach((req) => {
        if (req.location_id) {
          locationMap.set(req.location_id, req.location_address);
        }
        req.items.forEach((item: any) => {
          const itemName = item.name || item.product_name || item.label || item.item_name || "";
          const key = item.item_id || item.id || itemName || String(Math.random());
          if (!grouped.has(key)) {
            grouped.set(key, {
              item_id: item.item_id || item.id || "",
              item_name: itemName,
              unit: item.unit || "szt",
              total_quantity: 0,
              locations: [],
            });
          }
          const group = grouped.get(key)!;
          group.total_quantity += item.requested_qty || item.quantity || item.qty || 0;
          if (req.location_id) {
            const existingLocation = group.locations.find(
              (loc) => loc.location_id === req.location_id
            );
            if (existingLocation) {
              existingLocation.quantity += item.requested_qty || item.quantity || item.qty || 0;
            } else {
              group.locations.push({
                location_id: req.location_id,
                location_address: req.location_address,
                quantity: item.requested_qty || item.quantity || item.qty || 0,
                request_id: req.id,
              });
            }
          }
        });
      });

      setGroupedRequests(Array.from(grouped.values()));
    } catch (error) {
      console.error("Error fetching material requests:", error);
      toast.error(t("admin.warehouse.requestsFetchError"));
    }
  }, [currentOrgId, t]);

  useEffect(() => {
    // CRITICAL: Zapobieganie resetowaniu danych przy remount komponentu
    // Jeśli currentOrgId się nie zmienił i dane już zostały załadowane, nie resetuj
    const orgIdChanged = previousOrgIdRef.current !== currentOrgId;
    
    if (!currentOrgId) {
      // Brak orgId - wyczyść dane
      setCatalogItems([]);
      setMaterialRequests([]);
      setGroupedRequests([]);
      previousOrgIdRef.current = null;
      hasInitializedRef.current = false;
      return;
    }
    
    // Jeśli orgId się nie zmienił i już zainicjalizowano, nie resetuj
    if (!orgIdChanged && hasInitializedRef.current) {
      console.log('[WarehouseManagement] currentOrgId nie zmienił się - zachowuję istniejące dane');
      return;
    }
    
    // OrgId się zmienił lub pierwsza inicjalizacja - pobierz dane
    console.log('[WarehouseManagement] Pobieranie danych dla orgId:', currentOrgId, orgIdChanged ? '(zmiana orgId)' : '(pierwsza inicjalizacja)');
    previousOrgIdRef.current = currentOrgId;
    setIsLoading(true);
    
    Promise.all([
      executeWithRetry(() => fetchCatalog(), { isReadOperation: true }),
      executeWithRetry(() => fetchMaterialRequests(), { isReadOperation: true }),
    ])
      .catch((error) => {
        console.error("[WarehouseManagement] Błąd ładowania magazynu:", error);
      })
      .finally(() => {
        setIsLoading(false);
        hasInitializedRef.current = true;
      });
  }, [currentOrgId, fetchCatalog, fetchMaterialRequests]);

  // Bulletproof Handler: validate before spinner, try-catch-finally so spinner always turns off.
  const handleAddProduct = useCallback(async () => {
    if (!currentOrgId) {
      toast.error(t("admin.warehouse.missingOrg"));
      return;
    }
    if (!newProductName.trim()) {
      toast.error(t("admin.warehouse.enterProductName"));
      return;
    }

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("cleaning_catalog")
            .insert({
            org_id: currentOrgId,
            name: newProductName.trim(),
            unit: newProductUnit,
          }),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success(t("admin.warehouse.productAdded"));
      setNewProductName("");
      setNewProductUnit("szt");
      setIsAddProductOpen(false);
      await executeWithRetry(() => fetchCatalog(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error adding product:", error);
      toast.error(error?.message ?? t("admin.warehouse.addProductError"));
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, newProductName, newProductUnit, fetchCatalog, t]);

  const handleDeleteProduct = useCallback(async () => {
    if (!currentOrgId) {
      toast.error(t("admin.warehouse.missingOrg"));
      return;
    }
    if (!productToDelete) return;

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("cleaning_catalog")
            .delete()
            .eq("id", productToDelete)
            .eq("org_id", currentOrgId),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success(t("admin.warehouse.productDeleted"));
      setProductToDelete(null);
      setIsDeleteDialogOpen(false);
      await executeWithRetry(() => fetchCatalog(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error deleting product:", error);
      toast.error(error?.message ?? t("admin.warehouse.deleteProductError"));
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, productToDelete, fetchCatalog, t]);

  const handleMarkAsDelivered = useCallback(async () => {
    if (!currentOrgId) {
      toast.error(t("admin.warehouse.missingOrg"));
      return;
    }

    const requestIds = materialRequests.map((req) => req.id);
    if (requestIds.length === 0) {
      toast.info(t("admin.warehouse.nothingToMark"));
      return;
    }

    setIsActionLoading(true);
    try {
      const { error } = await executeWithRetry(
        async () =>
          await supabase
            .from("material_requests")
            .update({ status: "completed" })
            .in("id", requestIds)
            .eq("org_id", currentOrgId)
            .eq("status", "pending"),
        { isReadOperation: false }
      );

      if (error) throw error;

      toast.success(t("admin.warehouse.markedDelivered", { count: requestIds.length }));
      await executeWithRetry(() => fetchMaterialRequests(), { isReadOperation: true });
    } catch (error: any) {
      console.error("Error marking as delivered:", error);
      toast.error(error?.message ?? t("admin.warehouse.markDeliveredError"));
    } finally {
      setIsActionLoading(false);
    }
  }, [currentOrgId, materialRequests, fetchMaterialRequests, t]);

  const generateShoppingList = () => {
    return groupedRequests
      .map((item) => `${item.item_name || t("admin.warehouse.unnamedProduct")}: ${item.total_quantity} ${item.unit}`)
      .join("\n");
  };

  const handleCopyShoppingList = () => {
    const list = generateShoppingList();
    navigator.clipboard.writeText(list);
    toast.success(t("admin.warehouse.listCopied"));
  };

  const handlePrintShoppingList = () => {
    const list = generateShoppingList();
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${t("admin.warehouse.printTitle")}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { margin-bottom: 20px; }
              pre { font-size: 14px; line-height: 1.6; }
            </style>
          </head>
          <body>
            <h1>${t("admin.warehouse.printTitle")}</h1>
            <pre>${list}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.warehouse.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{t("admin.warehouse.loading")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Katalog Produktów */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("admin.warehouse.catalogTitle")}</CardTitle>
              <CardDescription>
                {t("admin.warehouse.catalogDescription")}
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddProductOpen(true)} disabled={isActionLoading}>
              {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {t("admin.warehouse.addProduct")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {catalogItems.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {t("admin.warehouse.emptyCatalog")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.warehouse.productName")}</TableHead>
                  <TableHead>{t("admin.warehouse.unit")}</TableHead>
                  <TableHead className="w-[100px]">{t("admin.warehouse.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isActionLoading}
                        onClick={() => {
                          setProductToDelete(item.id);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Aktywne Zapotrzebowania */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("admin.warehouse.activeTitle")}</CardTitle>
              <CardDescription>
                {t("admin.warehouse.activeDescription")}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsShoppingListOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {t("admin.warehouse.generateList")}
              </Button>
              <Button onClick={handleMarkAsDelivered} disabled={isActionLoading}>
                {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {t("admin.warehouse.markDelivered")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {groupedRequests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {t("admin.warehouse.emptyRequests")}
            </p>
          ) : (
            <div className="space-y-4">
              {groupedRequests.map((group) => (
                <Card key={group.item_id || group.item_name}>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {group.item_name || t("admin.warehouse.unnamedProduct")}: {group.total_quantity} {group.unit}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t("admin.warehouse.locations")}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {group.locations.map((loc, idx) => (
                          <li key={idx} className="text-sm">
                            {loc.location_address || t("admin.warehouse.unknownLocation")}: {loc.quantity} {group.unit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista zamówień (per request) z notatkami */}
      {materialRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.warehouse.orderDetails")}</CardTitle>
            <CardDescription>
              {t("admin.warehouse.orderDetailsDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {materialRequests.map((request) => (
                <div
                  key={request.id}
                  className="border border-border/60 rounded-lg p-4 space-y-2"
                >
                  <p className="text-sm font-medium text-muted-foreground">
                    {request.location_address || t("admin.warehouse.unknownLocation")}
                  </p>
                  {request.items && request.items.length > 0 ? (
                    <div className="space-y-1">
                      {request.items.map((item: any, i: number) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="font-medium">
                            {item.name || item.product_name || item.label || item.item_name || t("admin.warehouse.unnamedProduct")}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            x{item.quantity ?? item.qty ?? item.requested_qty ?? 0}
                            {item.unit ? ` ${item.unit}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("admin.warehouse.noProducts")}</p>
                  )}
                  {request.notes && (
                    <div className="mt-2 text-sm bg-muted/50 p-2 rounded-md border border-border/50">
                      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        {t("admin.warehouse.note")}
                      </span>
                      <p className="text-foreground mt-0.5">{request.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Dodaj produkt */}
      <Dialog open={isAddProductOpen} onOpenChange={setIsAddProductOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.warehouse.addProductTitle")}</DialogTitle>
            <DialogDescription>
              {t("admin.warehouse.addProductDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">{t("admin.warehouse.productName")}</Label>
              <Input
                id="product-name"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder={t("admin.warehouse.productNamePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-unit">{t("admin.warehouse.unit")}</Label>
              <Select
                value={newProductUnit}
                onValueChange={setNewProductUnit}
              >
                <SelectTrigger id="product-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="szt">{t("admin.warehouse.unitPiece")}</SelectItem>
                  <SelectItem value="litr">{t("admin.warehouse.unitLiter")}</SelectItem>
                  <SelectItem value="kg">{t("admin.warehouse.unitKg")}</SelectItem>
                  <SelectItem value="m">{t("admin.warehouse.unitMeter")}</SelectItem>
                  <SelectItem value="m²">{t("admin.warehouse.unitSquare")}</SelectItem>
                  <SelectItem value="opak">{t("admin.warehouse.unitPack")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddProductOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleAddProduct} disabled={isActionLoading}>
              {isActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t("admin.modals.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Lista zakupowa */}
      <Dialog open={isShoppingListOpen} onOpenChange={setIsShoppingListOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("admin.warehouse.printTitle")}</DialogTitle>
            <DialogDescription>
              {t("admin.warehouse.shoppingDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg">
              <pre className="whitespace-pre-wrap font-mono text-sm">
                {generateShoppingList()}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCopyShoppingList}>
              <Copy className="h-4 w-4 mr-2" />
              {t("admin.warehouse.copy")}
            </Button>
            <Button variant="outline" onClick={handlePrintShoppingList}>
              <Printer className="h-4 w-4 mr-2" />
              {t("admin.warehouse.print")}
            </Button>
            <Button onClick={() => setIsShoppingListOpen(false)}>{t("admin.warehouse.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog: Usuń produkt */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.warehouse.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.warehouse.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProduct} disabled={isActionLoading}>
              {t("admin.warehouse.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
